import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import AddressAutocomplete from '../components/shared/AddressAutocomplete';
import { ESTIMATE_REQUEST_STATUS } from '../constants/statuses';
import { isInServiceArea, isComingSoon } from '../constants/serviceArea';
import { normalizePhone } from '../lib/format';
import { PROMO, isPromoActive } from '../config/promoConfig';
import './FormPage.css';

const MAX_PHOTOS = 3;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // 10 MB

export default function Estimate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]); // array of { file, previewUrl }
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: null,
    scope: '',
    timing: '',
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    return () => photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back link returns to homepage pricing section
  const goToPricing = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('pricing');
      if (!el) return;
      const NAV_HEIGHT = window.innerWidth <= 768 ? 110 : 90;
      const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_HEIGHT;
      window.scrollTo({ top: y, behavior: 'instant' });
    }, 50);
  };

  // Logo goes to the very top of the homepage
  const goToTop = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };

  const onField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (error) setError('');
  };

  const onAddress = (addr) => {
    setForm((f) => ({ ...f, address: addr }));
    if (error) setError('');
  };

  const onPhotosSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_PHOTOS} photos.`);
      return;
    }

    const accepted = [];
    for (const file of files.slice(0, remaining)) {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are accepted.');
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        setError('Each photo must be under 10 MB.');
        continue;
      }
      accepted.push({ file, previewUrl: URL.createObjectURL(file) });
    }
    setPhotos((p) => [...p, ...accepted]);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((p) => {
      URL.revokeObjectURL(p[idx].previewUrl);
      return p.filter((_, i) => i !== idx);
    });
  };

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 10) {
      return 'Please enter a valid phone number.';
    }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      return 'Please enter a valid email.';
    }
    if (!form.address || !form.address.formatted) {
      return 'Please select your address from the suggestions.';
    }
    if (!form.scope.trim() || form.scope.trim().length < 10) {
      return 'Please describe the project (a sentence or two is plenty).';
    }
    if (!form.timing.trim()) {
      return 'Please tell us your preferred site visit window.';
    }
    if (photos.length === 0) {
      return 'Please add at least one photo of the project.';
    }
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setSubmitting(true);
    setError('');
    try {
      const zip = form.address?.zip || '';

      // Generate a doc ref + ID up front (does NOT create the doc).
      // This gives us a stable ID for the storage path before uploading.
      const { doc: docFn, setDoc } = await import('firebase/firestore');
      const newDocRef = docFn(collection(db, 'estimateRequests'));

      // Upload photos first using the pre-generated ID
      const uploadedUrls = [];
      for (let i = 0; i < photos.length; i++) {
        const { file } = photos[i];
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `estimateRequests/${newDocRef.id}/${Date.now()}_${i}_${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, { contentType: file.type });
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      }

      // Now create the doc with photoUrls already populated — single write.
      await setDoc(newDocRef, {
        createdAt: serverTimestamp(),
        status: ESTIMATE_REQUEST_STATUS.NEW,
        source: 'marketing-form',
        name: form.name.trim(),
        phone: normalizePhone(form.phone),
        email: form.email.trim().toLowerCase(),
        address: form.address,
        scopeDescription: form.scope.trim(),
        preferredVisitWindows: form.timing.trim() ? [form.timing.trim()] : [],
        photoUrls: uploadedUrls,
        inServiceArea: isInServiceArea(zip),
        comingSoon: isComingSoon(zip),
        notes: '',
        estimateId: null,
      });

      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Estimate request submission failed', err);
      setError('Something went wrong on our end. Please try again or call us at (516) 386-9923.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rh-formpage">
        {/* ── Top bar (matches /services page) ── */}
        <header className="svc-topbar">
          <a href="/#pricing" onClick={goToPricing} className="svc-back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </a>
          <a href="/" onClick={goToTop} className="svc-brand">
            <img src={require('../assets/logo2.png')} alt="Reliable Handyman" className="svc-brand-img" />
          </a>
          <div className="svc-topbar-spacer" aria-hidden="true" />
        </header>

        <div className="rh-formpage__inner rh-formpage__success">
          <div className="rh-success-card">
            <div className="rh-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" fill="#EAF7F0" />
                <path d="M7 12.5l3.5 3.5L17 9" stroke="#1D6B45" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1>Thanks, {form.name.split(' ')[0]}.</h1>
            <p>
              We&rsquo;ve got your request. We&rsquo;ll review the scope and reach out within
              one business day to schedule a quick walk-through (or send a quote from photos
              if it&rsquo;s straightforward). Questions?{' '}
              <a href="tel:+15163869923">(516) 386-9923</a>.
            </p>
            <div className="rh-success-actions">
              <button onClick={goToPricing} className="rh-form-btn">Back to pricing</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rh-formpage">
      {/* ── Top bar (matches /services page) ── */}
      <header className="svc-topbar">
        <a href="/#pricing" onClick={goToPricing} className="svc-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </a>
        <a href="/" onClick={goToTop} className="svc-brand">
          <img src={require('../assets/logo2.png')} alt="Reliable Handyman" className="svc-brand-img" />
        </a>
        <div className="svc-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="rh-formpage__inner">
        <header className="rh-formpage__header">
          <div className="rh-formpage__eyebrow">Request an Estimate</div>
          <h1>Tell us about your project.</h1>
          <p className="rh-formpage__lede">
            For larger or multi-task projects, we&rsquo;ll send a written estimate after a
            quick walk-through &mdash; or just from your photos if it&rsquo;s simple. Free,
            no obligation.
          </p>
        </header>

        {isPromoActive() && (
          <div className="rh-promo-notice">
            <div className="rh-promo-notice__tag">{PROMO.eyebrowLabel}</div>
            <div className="rh-promo-notice__body">
              All new customers receive {PROMO.percentOff}% off (up to ${PROMO.capDollars}) when payment is collected. Prices shown don't reflect this discount.
            </div>
          </div>
        )}

        {/* ── Flat-rate redirect prompt ── */}
        <div className="rh-flatrate-prompt">
          <div className="rh-flatrate-prompt__body">
            <div className="rh-flatrate-prompt__eyebrow">Quick question</div>
            <h2 className="rh-flatrate-prompt__title">Does your project sound like a flat-rate service?</h2>
            <p className="rh-flatrate-prompt__copy">
              We have firm prices for common jobs &mdash; faucets, fixtures, drywall patches, ceiling fans, and more. No estimate needed.
            </p>
          </div>
          <Link to="/services" state={{ from: 'estimate' }} className="rh-flatrate-prompt__btn">
            See flat-rate menu
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        <div className="rh-flatrate-divider">
          <span>Or fill out the form below for a custom project</span>
        </div>

        <form className="rh-form" onSubmit={onSubmit} noValidate>
          <div className="rh-form-grid">
            <div className="rh-field">
              <label htmlFor="name">Your name</label>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={onField('name')}
                autoComplete="name"
                placeholder="Jane Smith"
                required
              />
            </div>

            <div className="rh-field">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={onField('phone')}
                autoComplete="tel"
                placeholder="(516) 555-1234"
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={onField('email')}
                autoComplete="email"
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="address">Project address</label>
              <AddressAutocomplete
                id="address"
                value={form.address}
                onChange={onAddress}
                placeholder="Start typing your address..."
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="scope">Project description</label>
              <textarea
                id="scope"
                rows={6}
                value={form.scope}
                onChange={onField('scope')}
                placeholder="What needs to get done? The more detail you can share, the more accurate the estimate."
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="timing">Preferred site visit window</label>
              <input
                id="timing"
                type="text"
                value={form.timing}
                onChange={onField('timing')}
                placeholder="e.g. Weekday mornings, or any weekend afternoon"
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label>Photos <span className="rh-field__optional">(up to {MAX_PHOTOS})</span></label>
              <div className="rh-photo-row">
                {photos.map((p, i) => (
                  <div className="rh-photo-thumb" key={i}>
                    <img src={p.previewUrl} alt={`upload ${i + 1}`} />
                    <button
                      type="button"
                      className="rh-photo-remove"
                      onClick={() => removePhoto(i)}
                      aria-label="Remove photo"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <label className="rh-photo-add">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onPhotosSelected}
                      style={{ display: 'none' }}
                    />
                    <span>+ Add photo</span>
                  </label>
                )}
              </div>
              <div className="rh-field__hint">
                Photos help us scope the work faster and often save you a site visit.
              </div>
            </div>
          </div>

          {error && (
            <div className="rh-form-error" role="alert">{error}</div>
          )}

          <div className="rh-form-actions">
            <button
              type="submit"
              className="rh-form-btn"
              disabled={submitting}
            >
              {submitting ? (photos.length ? 'Uploading photos...' : 'Sending...') : 'Send request'}
            </button>
            <div className="rh-form-disclaimer">
              Estimates are free and there&rsquo;s no obligation. By submitting,
              you agree we can contact you about your project by phone, email,
              or text. Message and data rates may apply; reply STOP to opt out
              of texts. See our <Link to="/privacy">Privacy Policy</Link> and{' '}
              <Link to="/terms">Terms</Link>.
            </div>
          </div>
        </form>
      </div>

      {/* ── Footer (matches marketing site + /services) ── */}
      <footer className="svc-marketing-footer">
        <div className="svc-container svc-marketing-footer-inner">
          <a href="/" onClick={goToTop} className="svc-marketing-footer-brand" style={{ textDecoration: 'none' }}>
            <img src={require('../assets/logo2.png')} alt="Reliable Handyman" style={{ height: 26 }} />
          </a>
          <div className="svc-marketing-footer-contact">
            <a href="tel:+15163869923">(516) 386-9923</a>
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
